// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import {
    LosslessNumber,
    isLosslessNumber,
    isSafeNumber,
    parse,
    stringify,
    type DuplicateKeyInfo,
    type NumberStringifier,
    type Replacer,
} from 'lossless-json';

export { LosslessNumber, isLosslessNumber };

export type LosslessJsonReplacer = Replacer;

export type LosslessJsonPrimitive = null | boolean | string | number | LosslessNumber;

export type LosslessJsonArray = LosslessJsonValue[];

export type LosslessJsonObject = {
    [key: string]: LosslessJsonValue;
};

export type LosslessJsonValue = LosslessJsonPrimitive | LosslessJsonArray | LosslessJsonObject;

export const SemanticLosslessNumberRepresentationKeys: ReadonlySet<string> =
    new Set(['isLosslessNumber', 'value']);

export const SemanticJsonNumberTokenPattern: RegExp =
    /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/u;

export function isSemanticLosslessNumber(
    value: unknown
): value is LosslessNumber {
    if (!isLosslessNumber(value)) {
        return false;
    }
    if (!SemanticJsonNumberTokenPattern.test(value.value)) {
        return false;
    }
    if (
        !Object.keys(value).every((key: string) =>
            SemanticLosslessNumberRepresentationKeys.has(key)
        )
    ) {
        return false;
    }
    try {
        return value.toString() === value.value;
    } catch {
        return false;
    }
}

export class LosslessJsonSerializationError extends TypeError {
    constructor(message: string, options?: ErrorOptions) {
        super(message, options);
        this.name = 'LosslessJsonSerializationError';
    }
}

export const StrictLosslessNumberStringifier: NumberStringifier = {
    test: isLosslessNumber,
    stringify: (value: unknown): string => {
        if (!isSemanticLosslessNumber(value)) {
            throw new TypeError('Invalid lossless JSON number representation');
        }
        return value.value;
    },
};

export function parseLosslessJson(jsonText: string): unknown {
    return parse(jsonText);
}

function parseNativeSafeNumber(numberToken: string): number | LosslessNumber {
    if (isSafeNumber(numberToken, { approx: false })) {
        return Number.parseFloat(numberToken);
    }
    return new LosslessNumber(numberToken);
}

function useLastDuplicateJsonValue(duplicateKey: DuplicateKeyInfo): unknown {
    return duplicateKey.newValue;
}

export function parseJsonWithNativeSafeNumbers(jsonText: string): unknown {
    return parse(jsonText, null, {
        onDuplicateKey: useLastDuplicateJsonValue,
        parseNumber: parseNativeSafeNumber,
    });
}

export function stringifyLosslessJson(
    value: unknown,
    replacer: Replacer | null = null,
    space?: number | string
): string {
    let jsonText: string | undefined;
    try {
        jsonText = stringify(value, replacer, space, [
            StrictLosslessNumberStringifier,
        ]);
    } catch (cause) {
        throw new LosslessJsonSerializationError('Failed to serialize value as a JSON document', {
            cause,
        });
    }
    if (jsonText === undefined) {
        throw new LosslessJsonSerializationError('Value cannot be serialized as a JSON document');
    }
    return jsonText;
}
